import { makeAutoObservable, computed } from 'mobx';
import { WordSayerInterface } from '@/models/word-sayer-interface';
import { WordToFind } from './word-to-find';
import { WordToChoose } from './word-to-choose';
import { ButtonAction } from '@/lib/models/actions';
import { ValueModel } from '@/lib/models/value-models';
import { ConfirmationModel } from '@/lib/models/confirmation';
import { EmotionalWordSayer } from '@/models/audio/emotional-word-sayer';
import { HappyOrSad } from '@/models/audio/emotion-types';
import { shuffle } from '@/lib/util';

export class WordChoiceFinder {
  wordSayer: WordSayerInterface;
  words: string[];
  wordsToFind: WordToFind[];
  wordChangerToFind: WordToFind | null = null;
  wordsToChoose: WordToChoose[];
  correct: number = 0;
  tried: number = 0;
  newWordsCallback: () => string[];
  auto: ValueModel<boolean>;
  emotionalWordSayer: EmotionalWordSayer<HappyOrSad>;
  confirmation: ConfirmationModel;

  constructor(wordSayer: WordSayerInterface, words: string[], newWordsCallback: () => string[], emotionalWordSayer: EmotionalWordSayer<HappyOrSad>) {
    this.wordSayer = wordSayer;
    this.newWordsCallback = newWordsCallback;
    this.emotionalWordSayer = emotionalWordSayer;

    this.words = words.slice();

    this.wordsToFind = this.words.map(word => new WordToFind(this, word));
    this.wordsToChoose = shuffle(this.words).map(word => new WordToChoose(this, word));

    // Initialize auto checkbox (defaulted to checked)
    this.auto = new ValueModel(true, 'Auto', 'Automatically choose next word to find');

    // Initialize confirmation model
    this.confirmation = new ConfirmationModel();

    makeAutoObservable(this, {
      numWordsChosen: computed,
      finished: computed,
      scoreText: computed,
      retryAction: computed,
      newAction: computed,
      allWordsAttempted: computed
    });
  }

  get numWordsChosen(): number {
    return this.wordsToFind.filter(w => w.state === 'right' || w.state === 'wrong').length;
  }

  get finished(): boolean {
    return this.numWordsChosen === this.wordsToFind.length;
  }

  get scoreText(): string {
    return `${this.correct} / ${this.tried}`;
  }

  get allWordsAttempted(): boolean {
    return this.numWordsChosen === this.wordsToFind.length;
  }

  get retryAction(): ButtonAction {
    const handler = this.finished ? () => this.retry() : null;
    return new ButtonAction(handler, { tooltip: "Retry with the same words" });
  }

  get newAction(): ButtonAction {
    const handler = () => this.requestNew();
    return new ButtonAction(handler, { tooltip: "Start with a new set of words" });
  }

  async requestNew(): Promise<void> {
    if (this.allWordsAttempted) {
      // All words have been attempted, proceed directly
      this.new();
    } else {
      // Not all words attempted, ask for confirmation
      await this.confirmation.askForConfirmationYesOrNo(
        'Are you sure you want to quit and start again with new words to find?',
        () => this.new()
      );
    }
  }

  setWordChangerToFind(wordToFind: WordToFind): void {
    if (this.wordChangerToFind && this.wordChangerToFind !== wordToFind) {
      this.wordChangerToFind.state = 'waiting';
    }
    this.wordChangerToFind = wordToFind;
    wordToFind.state = 'current';
  }

  clearWordChangerToFind(): void {
    this.wordChangerToFind = null;

    // If auto is enabled, choose next word after delay
    if (this.auto.value) {
      setTimeout(() => {
        this.autoChooseNextWord();
      }, 300);
    }
  }

  private async autoChooseNextWord(): Promise<void> {
    const waitingWords = this.wordsToFind.filter(w => w.state === 'waiting');
    if (waitingWords.length > 0) {
      const randomIndex = Math.floor(Math.random() * waitingWords.length);
      const nextWord = waitingWords[randomIndex];
      this.setWordChangerToFind(nextWord);
      await this.wordSayer.say(nextWord.word);
    }
  }


  incrementCorrect(): void {
    this.correct++;
    this.tried++;
    this.checkFinished();
  }

  incrementTried(): void {
    this.tried++;
    this.checkFinished();
  }

  private async checkFinished(): Promise<void> {
    if (this.finished) {
      if (this.correct === this.wordsToFind.length) {
        // Play a random happy word for perfect score
        await this.emotionalWordSayer.playRandomWord('happy');
      }
    }
  }

  retry(): void {
    this.wordChangerToFind = null;
    this.correct = 0;
    this.tried = 0;

    this.wordsToFind.forEach(wordToFind => {
      wordToFind.state = 'waiting';
    });
  }

  new(): void {
    const newWords = this.newWordsCallback();
    this.setWords(newWords);
  }

  setWords(words: string[]): void {
    this.words = words.slice();
    this.wordsToFind = this.words.map(word => new WordToFind(this, word));
    this.wordsToChoose = shuffle(this.words).map(word => new WordToChoose(this, word));
    // Keep the auto setting when setting new words
    this.retry();
  }
}
